import { Empty, Input, Select } from "antd";
import IconButton from "components/common/IconButton";
import useMessageTemplates from "hooks/message-templates/useMessageTemplates";
import useDebounce from "hooks/route-rules/useDebounce";
import React, { useEffect, useMemo, useState } from "react";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import TemplatesTable from "./TemplatesTable";
import { Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

export default function MessageTemplates() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCode, setSelectedCode] = useState("ALL");
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const isSearchRequest = debouncedSearchTerm.trim().length >= 3;
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const {
    loading,
    totalPages,
    totalElements,
    messageTemplates,
    deleteTemplate,
    searchTemplates,
  } = useMessageTemplates(page, pageSize);
  const codeOptions = useMemo(() => {
    const uniqueCodes = Array.from(
      new Set(
        (messageTemplates || [])
          .map((item) => String(item?.code || "").trim())
          .filter(Boolean),
      ),
    );
    return [
      { label: "🌐 All Template Codes", value: "ALL" },
      ...uniqueCodes.map((code) => ({ label: `🧩 ${code}`, value: code })),
    ];
  }, [messageTemplates]);

  useEffect(() => {
    const trimmed = debouncedSearchTerm.trim();
    const hasSearchTerm = trimmed.length >= 3;
    const hasCodeFilter =
      selectedCode.trim().length > 0 && selectedCode.trim() !== "ALL";

    if (!hasSearchTerm && !hasCodeFilter) {
      return;
    }

    searchTemplates(trimmed, page, selectedCode);
  }, [debouncedSearchTerm, page, searchTemplates, selectedCode]);

  return (
    <>
      <WidgetSectionTitle
        icon="envelope-open-text"
        title="Message Templates"
        subtitle="Manage email and sms templates"
        transform="shrink-2"
        className="mb-3 mt-3"
      />

      <div className="d-flex justify-content-end mb-3">
        <IconButton
          variant="success"
          size="sm"
          icon="plus"
          transform="shrink-3"
          onClick={() => navigate(`/account/message-templates/create`)}
          className="text-xs fw-bold"
          style={{ color: "#fff" }}
        >
          <span className="ms-1">Add New Template</span>
        </IconButton>
      </div>

      <div className="mb-2">
        <div
          className="mb-3 align-items-end gx-3 p-2 rounded bg-light row mx-auto"
          style={{ maxWidth: 980 }}
        >
          <div className="col-md-8">
            <div className="text-muted small fw-semibold mb-1">
              {"\u{1F50E} Search Templates"}
            </div>
            <Input
              placeholder='Search by "subject" or "body" (min. 3 chars)...'
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(0);
              }}
              allowClear
              style={{
                width: "63%",
                borderRadius: 24,
                borderColor:
                  searchTerm.length > 0 && searchTerm.length < 3
                    ? "#ffa940"
                    : "",
              }}
              suffix={
                loading && isSearchRequest ? (
                  <Spinner animation="border" size="sm" variant="success" />
                ) : (
                  searchTerm.length > 0 &&
                  searchTerm.length < 3 && (
                    <small className="text-warning" style={{ fontSize: "10px" }}>
                      Type {3 - searchTerm.length} more...
                    </small>
                  )
                )
              }
            />
          </div>
          <div className="col-md-4">
            <div className="text-muted small fw-semibold mb-1">
              {"\u{1F3F7}\uFE0F Template Code Filter"}
            </div>
            <Select
              style={{ width: "100%" }}
              value={selectedCode}
              placeholder="Filter by template code"
              showSearch={{ optionFilterProp: "label" }}
              popupMatchSelectWidth={false}
              styles={{ popup: { root: { minWidth: 320 } } }}
              onChange={(value) => {
                setSelectedCode(value || "ALL");
                setPage(0);
              }}
              options={codeOptions}
            />
          </div>
        </div>
      </div>

      {loading && messageTemplates.length === 0 ? (
        <div
          style={{
            minHeight: 200,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Spinner animation="border" variant="success" />
        </div>
      ) : messageTemplates && messageTemplates.length === 0 ? (
        <div
          style={{
            minHeight: 200,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Empty
            description={
              searchTerm.trim() &&
              selectedCode.trim() &&
              selectedCode.trim() !== "ALL"
                ? `No templates found for search "${searchTerm.trim()}" with code "${selectedCode.trim()}".`
                : searchTerm.trim()
                ? `No templates with the search term "${searchTerm.trim()}" were found`
                : selectedCode.trim() && selectedCode.trim() !== "ALL"
                ? `No templates with code "${selectedCode.trim()}" were found`
                : "No templates were found"
            }
          />
        </div>
      ) : (
        <TemplatesTable
          templates={messageTemplates}
          deleteTemplate={deleteTemplate}
          page={page}
          pageSize={pageSize}
          setPage={setPage}
          setPageSize={setPageSize}
          totalPages={totalPages}
          totalElements={totalElements}
        />
      )}
    </>
  );
}
